import moment from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import StatusDotProfileCmp from 'src/components/StatusDotProfileCmp';
import TooltipCmp from 'src/components/TooltipCmp';

import useTrackClock from './hooks/useTrackClock';

interface IProps {
    // Extra classes so each surface can adjust spacing (the map view's floating
    // header sits tighter than the table view's).
    className?: string;
}

// Current date + time for the Track header, in `appearance.timezone` from the
// basic slice (via useTrackClock) — the same dynamic field every other date on the
// Track page uses. In the portal that field is the reader's resolved clock.
//
// Self-updating on the second via useTrackClock — no page refresh needed.
// Ported from wal-frontend-v2 src/app/Radar/components/RadarHeaderClock.tsx.
const TrackHeaderClock: React.FC<IProps> = ({ className = '' }) => {
    const { t } = useTranslation();
    const { now, timezone } = useTrackClock();

    // moment.tz throws on an unknown/undefined zone name; fall back to the
    // browser's local time rather than blanking the clock while `appearance` is
    // still loading (fetchUserData populates it a tick after first render).
    const current = timezone && moment.tz.zone(timezone) ? moment(now).tz(timezone) : moment(now);

    // Day + its ordinal suffix are rendered as separate nodes so the suffix can be
    // a real <sup> element. The codebase's getDateWithSuffixFormat helpers return an
    // HTML string needing dangerouslySetInnerHTML; building the JSX directly gives
    // the same superscript without that.
    const day = current.format('D');
    const daySuffix = current.format('Do').replace(day, '');

    return (
        // The clock itself stays visible (it's the primary info); the tooltip only
        // names WHICH timezone is being shown, which the abbreviation alone leaves
        // ambiguous (several zones share "EDT"-style abbreviations).
        <TooltipCmp message={timezone ? t('Radar.Viewer_Timezone', { timezone }) : null} childrenClassName="flex">
            {/* Modern BadgeCmp = the design's white pill (rounded-md + borderPrimary border).
                Only the values this pill differs on are overridden: the badge's default drop
                shadow → shadow-xs, plus the white fill and the 6px/2px padding. `isHidePillDot`
                turns off the badge's own 6px flat dot in favour of the shared status dot below,
                which is the one that carries the design's light-green ring. */}
            <BadgeCmp style="modern" isHidePillDot mainClassName={`track-header-clock !bg-white !shadow-xs !pr-1.5 pl-1.5 !ml-0 !py-0.5 whitespace-nowrap ${className}`}>
                {/* Same live-status dot the status rows use (green = ticking). Passing the
                    clock as its `label` also gives us the 6px dot-to-text gap for free. */}
                <StatusDotProfileCmp
                    dotColor="green"
                    statusLabelClassName="!text-[11px] leading-4 font-medium normal-case"
                    label={
                        <span className="flex items-center gap-x-1">
                            <span>
                                {current.format('ddd, MMM')} {day}
                                {/* leading-[0] keeps the raised suffix from stretching the pill's line box. */}
                                <sup className="text-[7px] leading-[0]">{daySuffix}</sup>
                            </span>
                            {/* A real 1px x 10px rule, not a "|" glyph — the design's divider is a shape,
                                so its height stays fixed instead of tracking the font's cap height.
                                gray300 (#E8E8E8) is this app's closest token to WAL's grayDark900 (#E3E3E3). */}
                            <span className="w-px h-2.5 flex-none bg-gray300" />
                            <span className="tabular-nums">{current.format('h:mm:ss A z')}</span>
                        </span>
                    }
                />
            </BadgeCmp>
        </TooltipCmp>
    );
};

export default TrackHeaderClock;
